import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useEvent } from "@/context/event-context";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Bell, Check, Loader2 } from "lucide-react";

interface WaitlistFormProps {
  tierId?: string;
  tablePackageId?: string;
  tierName: string;
}

export function WaitlistForm({ tierId, tablePackageId, tierName }: WaitlistFormProps) {
  const { event } = useEvent();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !event) return;

    setError("");
    setIsSubmitting(true);

    try {
      const { error: insertError } = await supabase.from("waitlist").insert({
        event_id: event.id,
        tier_id: tierId || null,
        table_package_id: tablePackageId || null,
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
        name: name.trim() || null,
      });

      if (insertError) {
        if (insertError.code === "23505") {
          // Unique constraint — already signed up
          setSubmitted(true);
        } else {
          throw insertError;
        }
      } else {
        setSubmitted(true);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 py-2">
        <Check className="w-4 h-4 text-green-600" />
        <span className="font-serif-elegant text-sm italic text-green-700">
          You're on the list for {tierName}. We'll notify you when spots open.
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2 pt-2">
      <p className="font-serif-elegant text-sm italic text-foreground/60">
        Get notified if {tierName} becomes available.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="font-sans text-sm h-9"
        />
        <Input
          type="email"
          placeholder="Email *"
          required
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(""); }}
          className="font-sans text-sm h-9"
        />
        <Input
          type="tel"
          placeholder="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="font-sans text-sm h-9"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          disabled={!email.trim() || isSubmitting}
          className="bg-ink text-white hover:bg-gold hover:text-white font-heading text-[0.7rem] uppercase tracking-[0.15em] h-9 px-4"
        >
          {isSubmitting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <>
              <Bell className="w-3.5 h-3.5 mr-1.5" />
              Join Waitlist
            </>
          )}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-red-600 font-sans">{error}</p>
      )}
    </form>
  );
}
